import { Skeleton, Stack, Box } from '@mui/material'

const ROW_HEIGHT = 36
const INDENT = 24
const LINE_COLOR = '#E5E7EB'

type TreeLineProps = {
  isLastChild: boolean
  showContinuousLine: boolean
}

const TreeLine = ({ isLastChild, showContinuousLine }: TreeLineProps) => (
  <Box
    sx={{
      position: 'relative',
      width: 20,
      height: ROW_HEIGHT,
      flexShrink: 0,
    }}
  >
    {/* Ligne verticale */}
    <Box
      sx={{
        position: 'absolute',
        left: 10,
        top: 0,
        bottom: isLastChild ? '50%' : 0,
        borderLeft: `1px solid ${LINE_COLOR}`,
      }}
    />
    {/* Ligne horizontale vers le nœud */}
    <Box
      sx={{
        position: 'absolute',
        left: 10,
        top: '50%',
        width: 10,
        borderTop: `1px solid ${LINE_COLOR}`,
        borderBottomLeftRadius: isLastChild ? 6 : 0,
        ...(isLastChild && {
          borderLeft: `1px solid ${LINE_COLOR}`,
          height: '50%',
          top: 0,
          borderTop: 'none',
        }),
      }}
    />
    {/* Ligne continue du parent */}
    {showContinuousLine && (
      <Box
        sx={{
          position: 'absolute',
          left: -14,
          top: 0,
          bottom: 0,
          borderLeft: `1px solid ${LINE_COLOR}`,
        }}
      />
    )}
  </Box>
)

const ToggleSkeleton = () => (
  <Skeleton
    variant="rounded"
    width={44}
    height={24}
    animation="wave"
    sx={{ borderRadius: 12, flexShrink: 0 }}
  />
)

type RootRowSkeletonProps = {
  showToggle?: boolean
}

const RootRowSkeleton = ({ showToggle = true }: RootRowSkeletonProps) => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{ height: ROW_HEIGHT, px: 1, py: 0.5 }}
  >
    {/* Icône bâtiment */}
    <Skeleton variant="rounded" width={20} height={20} animation="wave" sx={{ mr: 1, flexShrink: 0 }} />
    {/* Nom */}
    <Skeleton variant="text" width={90} height={20} animation="wave" sx={{ flexGrow: 0 }} />
    {/* Compteur */}
    <Skeleton variant="text" width={36} height={18} animation="wave" sx={{ ml: 1, mr: 'auto' }} />
    {/* Toggle */}
    {showToggle && <ToggleSkeleton />}
  </Stack>
)

type FloorRowSkeletonProps = {
  isLastChild: boolean
  showContinuousLine?: boolean
  showToggle?: boolean
}

const FloorRowSkeleton = ({ isLastChild, showContinuousLine = false, showToggle = true }: FloorRowSkeletonProps) => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{ height: ROW_HEIGHT, pl: `${INDENT}px`, pr: 1 }}
  >
    <TreeLine isLastChild={isLastChild} showContinuousLine={showContinuousLine} />
    {/* Icône étage (couches) */}
    <Skeleton variant="rounded" width={18} height={18} animation="wave" sx={{ mr: 1, flexShrink: 0 }} />
    {/* Nom étage */}
    <Skeleton variant="text" width={70} height={18} animation="wave" sx={{ mr: 'auto' }} />
    {/* Toggle */}
    {showToggle && <ToggleSkeleton />}
  </Stack>
)

type DeviceRowSkeletonProps = {
  isLastChild: boolean
  parentIsLastFloor: boolean
}

const DeviceRowSkeleton = ({ isLastChild, parentIsLastFloor }: DeviceRowSkeletonProps) => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{ height: ROW_HEIGHT, pl: `${INDENT * 2 + 4}px`, pr: 1 }}
  >
    <TreeLine isLastChild={isLastChild} showContinuousLine={!parentIsLastFloor} />
    {/* Nom device */}
    <Skeleton variant="text" width={50} height={16} animation="wave" sx={{ mr: 'auto' }} />
    {/* Toggle */}
    <ToggleSkeleton />
  </Stack>
)

const SkeletonLoader = () => {
  // Structure simulée : 1 bâtiment avec plusieurs étages, dont un avec des devices
  const structure = [
    { hasDevices: false },
    { hasDevices: true, deviceCount: 3 },
    { hasDevices: false },
    { hasDevices: false },
    { hasDevices: false },
    { hasDevices: false },
  ]

  return (
    <Stack sx={{ padding: '8px', minWidth: 280, flexShrink: 0, height: '100%' }}>
      {/* Élément racine (bâtiment) */}
      <RootRowSkeleton />

      {/* Étages */}
      {structure.map((floor, floorIndex) => {
        const isLastFloor = floorIndex === structure.length - 1

        return (
          <Box key={`floor-${floorIndex}`}>
            <FloorRowSkeleton isLastChild={isLastFloor && !floor.hasDevices} />

            {/* Devices de cet étage si présents */}
            {floor.hasDevices &&
              Array.from({ length: floor.deviceCount || 0 }).map((_, deviceIndex) => {
                const isLastDevice = deviceIndex === (floor.deviceCount || 0) - 1

                return (
                  <DeviceRowSkeleton
                    key={`device-${floorIndex}-${deviceIndex}`}
                    isLastChild={isLastDevice}
                    parentIsLastFloor={isLastFloor}
                  />
                )
              })}
          </Box>
        )
      })}
    </Stack>
  )
}

export default SkeletonLoader